"use client";

// ---------------------------------------------------------------------------
// thatradiothing socket — singleton, layout-scoped
//
// Why this file exists
// --------------------
// thatradiothing is a global resource: there is exactly one logical playback
// state per user, and we want exactly one Socket.IO connection per browser
// tab to mirror it.
//
// Before this provider, two different components opened their own sockets:
//
//   • ``SiteShell``                — for the header icon's "is the user
//                                    actively playing?" indicator on
//                                    non-home pages.
//   • ``ThatRadioThingClient``     — for the inline radio card on the home
//                                    page and the floating dock everywhere
//                                    else.
//
// Each component owned a ``useEffect`` that called ``io(...)`` and listened
// for the ``status`` event. That meant:
//
//   1. On the home page we held *two* websockets concurrently (one in
//      ``SiteShell``, one in ``ThatRadioThingClient``).
//   2. ``SiteShell``'s socket effect depended on ``isHomePage``,
//      ``radioClientOpen``, and ``radioClientDismissed``, so it re-ran —
//      tearing down and rebuilding the websocket — on every home <-> non-home
//      navigation and every dock toggle.
//
// Lifting the socket here fixes both: a single connection is opened once
// when the layout mounts (and the flag cookie says we're authenticated),
// kept alive across every client-side navigation within the language
// segment, and shared by every consumer that calls ``useThatRadioThingSocket``.
//
// What the provider does
// ----------------------
// On mount, if the ``duudey_logged_in`` flag cookie is present (the
// "local-truth" gate used everywhere else in the app — guests don't need a
// socket because they cannot be the playback master), it opens one
// Socket.IO connection to the thatradiothing origin. It listens for the
// ``status`` event and exposes the latest payload via React context. On
// unmount (full layout teardown — language switch, hard reload) it
// disconnects.
//
// The payload is left intentionally untyped beyond ``Record<string,
// unknown>``: the underlying REST shape lives in ``thatradiothing-client``
// and we don't want this module to dictate it. Consumers narrow the fields
// they need.
//
// Auth transitions
// ----------------
// Login and logout both trigger a full page navigation in our app
// (``window.location.reload()`` on logout, OAuth redirect on login), so the
// provider remounts and re-evaluates the flag cookie. We therefore don't
// need to listen for cookie changes at runtime — the provider is "open or
// closed" for the lifetime of a single mount.
// ---------------------------------------------------------------------------

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { io, type Socket } from "socket.io-client";

import { hasLoggedInFlagCookie } from "@/lib/auth/client-flag";
import { thatradiothingApiBase } from "@/lib/thatradiothing";

// Raw status payload from thatradiothing. We keep this loose on purpose —
// see the file header. Consumers that care about specific fields cast or
// narrow as they need.
export type ThatRadioThingStatus = Record<string, unknown>;

type ThatRadioThingSocketContextValue = {
  // The most recent ``status`` event payload, or ``null`` until the first
  // event lands (or always ``null`` for guests who never connect).
  status: ThatRadioThingStatus | null;
  // Whether the socket is currently connected. ``false`` covers both
  // "guest, no socket opened" and "socket opened but transport down".
  connected: boolean;
};

const DEFAULT_VALUE: ThatRadioThingSocketContextValue = {
  status: null,
  connected: false,
};

const ThatRadioThingSocketContext = createContext<ThatRadioThingSocketContextValue>(DEFAULT_VALUE);

/**
 * React hook that returns the latest thatradiothing ``status`` payload and
 * connection flag. Safe to call from any descendant of
 * ``ThatRadioThingSocketProvider``; outside the provider it returns the
 * default ``{ status: null, connected: false }`` so consumers can stay
 * unconditional.
 */
export function useThatRadioThingSocket(): ThatRadioThingSocketContextValue {
  return useContext(ThatRadioThingSocketContext);
}

/**
 * Layout-level provider that owns the single Socket.IO connection to
 * thatradiothing. Mount it once high in the React tree (e.g. in the
 * ``[lang]/layout.tsx`` server-component layout, wrapped by ``SiteShell``).
 */
export function ThatRadioThingSocketProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<ThatRadioThingStatus | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    // Local-truth gate. Guests cannot be the master, so they have nothing
    // for the socket to push them. Skip the connection entirely.
    if (typeof document === "undefined" || !hasLoggedInFlagCookie()) {
      return;
    }

    // ``transports: ["websocket", "polling"]`` lets Socket.IO negotiate
    // its own transport fallback — if websockets are blocked it
    // automatically downgrades to long-polling, so we don't need to layer
    // a second REST poll on top.
    const socket: Socket = io(thatradiothingApiBase(), {
      withCredentials: true,
      transports: ["websocket", "polling"],
    });

    const onStatus = (payload: ThatRadioThingStatus) => {
      setStatus(payload);
    };
    const onConnect = () => setConnected(true);
    const onDisconnect = () => {
      setConnected(false);
      // We deliberately keep the last ``status`` payload around during a
      // transient disconnect — the icon animation can stay accurate
      // through a momentary network blip rather than flickering off.
      // On full unmount we clear below.
    };

    socket.on("status", onStatus);
    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);

    return () => {
      socket.off("status", onStatus);
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.disconnect();
      setConnected(false);
      setStatus(null);
    };
  }, []);

  return (
    <ThatRadioThingSocketContext.Provider value={{ status, connected }}>
      {children}
    </ThatRadioThingSocketContext.Provider>
  );
}
